import type { RefObject } from "react";
import { useEffect, useRef, useState } from "react";

export function useIsSticky(ref: RefObject<HTMLElement>): boolean {
  const [isSticky, setIsSticky] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const el = ref.current;

    if (!el) return undefined;

    // Insert a sentinel element immediately before the sticky element and
    // observe it instead of the sticky element itself. This breaks a feedback
    // loop that causes jank on short pages: collapsing the header shrinks page
    // height → the browser clamps scrollY → the sticky condition flips →
    // header re-expands → repeat. The sentinel's document position is
    // unaffected by the header's own height changes, so its intersection state
    // is stable throughout the collapse animation.
    const sentinel = document.createElement("div");

    sentinel.setAttribute("aria-hidden", "true");
    sentinel.style.cssText = "height:1px;width:0;pointer-events:none;";
    el.parentNode?.insertBefore(sentinel, el);

    const getStickyTop = () => parseFloat(getComputedStyle(el).top) || 0;

    const createObserver = () => {
      observerRef.current?.disconnect();
      const stickyTop = getStickyTop();

      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          setIsSticky(!entry.isIntersecting);
        },
        {
          // Shrink the root viewport by stickyTop so the sentinel exits the
          // effective root exactly when the sticky element would pin to its
          // top offset.
          rootMargin: `-${stickyTop}px 0px 0px 0px`,
          threshold: 0,
        },
      );

      observerRef.current.observe(sentinel);
    };

    createObserver();

    // Recreate the observer when CSS variables on :root change so rootMargin
    // stays in sync with --header-height / --omnisearch-height.
    const mutationObserver = new MutationObserver(createObserver);

    mutationObserver.observe(document.documentElement, {
      attributeFilter: ["style"],
      attributes: true,
    });

    return () => {
      observerRef.current?.disconnect();
      mutationObserver.disconnect();
      sentinel.remove();
    };
  }, [ref]);

  return isSticky;
}
